/**
 * useAccessTypes Hooks
 *
 * React Query hooks para entidades de acesso:
 * - PersonImageType
 * - PersonRelationshipType
 * - FamilyRelationshipType
 *
 * @since 2025-12-20
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  personImageTypeApi,
  personRelationshipTypeApi,
  familyRelationshipTypeApi,
  listLoginLogs,
  listLoginLogActions,
  getLoginLogAggregation,
  personLoginApi,
} from '@/services/accessApi';
import { useAuth } from '@/contexts/AuthContext';
import type {
  PersonImageTypeCreate,
  PersonImageTypeUpdate,
  PersonRelationshipTypeCreate,
  PersonRelationshipTypeUpdate,
  FamilyRelationshipTypeCreate,
  FamilyRelationshipTypeUpdate,
  AccessListParams,
  LoginLogParams,
  PersonLoginListParams,
  PersonLoginCreate,
  PersonLoginUpdate,
} from '@/types/access';

// =============================================================================
// Query Keys
// =============================================================================

export const accessTypeKeys = {
  all: ['accessTypes'] as const,
  // Person Image Type
  imageTypes: () => [...accessTypeKeys.all, 'imageTypes'] as const,
  imageTypesList: (params?: AccessListParams) => [...accessTypeKeys.imageTypes(), 'list', params] as const,
  imageType: (id: number) => [...accessTypeKeys.imageTypes(), id] as const,
  // Person Relationship Type
  relationshipTypes: () => [...accessTypeKeys.all, 'relationshipTypes'] as const,
  relationshipTypesList: (params?: AccessListParams) => [...accessTypeKeys.relationshipTypes(), 'list', params] as const,
  relationshipType: (id: number) => [...accessTypeKeys.relationshipTypes(), id] as const,
  // Family Relationship Type
  familyRelationshipTypes: () => [...accessTypeKeys.all, 'familyRelationshipTypes'] as const,
  familyRelationshipTypesList: (params?: AccessListParams) => [...accessTypeKeys.familyRelationshipTypes(), 'list', params] as const,
  familyRelationshipType: (id: number) => [...accessTypeKeys.familyRelationshipTypes(), id] as const,
  // Login Logs
  loginLogs: () => [...accessTypeKeys.all, 'loginLogs'] as const,
  loginLogsList: (params?: LoginLogParams) => [...accessTypeKeys.loginLogs(), 'list', params] as const,
  loginLogActions: () => [...accessTypeKeys.loginLogs(), 'actions'] as const,
  loginLogAggregation: (params?: LoginLogParams) => [...accessTypeKeys.loginLogs(), 'aggregation', params] as const,
  // Person Logins
  personLogins: () => [...accessTypeKeys.all, 'personLogins'] as const,
  personLoginsList: (params?: PersonLoginListParams) => [...accessTypeKeys.personLogins(), 'list', params] as const,
  personLogin: (id: number) => [...accessTypeKeys.personLogins(), id] as const,
  availableProfiles: () => [...accessTypeKeys.personLogins(), 'availableProfiles'] as const,
};

// =============================================================================
// Person Image Type Hooks
// =============================================================================

/**
 * Hook para listar tipos de imagem de pessoa
 */
export function usePersonImageTypesList(params?: AccessListParams) {
  return useQuery({
    queryKey: accessTypeKeys.imageTypesList(params),
    queryFn: () => personImageTypeApi.list(params || {}),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para obter um tipo de imagem de pessoa
 */
export function usePersonImageType(id: number) {
  return useQuery({
    queryKey: accessTypeKeys.imageType(id),
    queryFn: () => personImageTypeApi.get(id),
    enabled: id > 0,
  });
}

/**
 * Hook para criar tipo de imagem de pessoa
 */
export function useCreatePersonImageType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PersonImageTypeCreate) => personImageTypeApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.imageTypes() });
    },
  });
}

/**
 * Hook para atualizar tipo de imagem de pessoa
 */
export function useUpdatePersonImageType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PersonImageTypeUpdate }) =>
      personImageTypeApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.imageType(id) });
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.imageTypesList() });
    },
  });
}

/**
 * Hook para deletar tipo de imagem de pessoa
 */
export function useDeletePersonImageType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => personImageTypeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.imageTypes() });
    },
  });
}

// =============================================================================
// Person Relationship Type Hooks
// =============================================================================

/**
 * Hook para listar tipos de relacionamento de pessoa
 */
export function usePersonRelationshipTypesList(params?: AccessListParams) {
  return useQuery({
    queryKey: accessTypeKeys.relationshipTypesList(params),
    queryFn: () => personRelationshipTypeApi.list(params || {}),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para obter um tipo de relacionamento de pessoa
 */
export function usePersonRelationshipType(id: number) {
  return useQuery({
    queryKey: accessTypeKeys.relationshipType(id),
    queryFn: () => personRelationshipTypeApi.get(id),
    enabled: id > 0,
  });
}

/**
 * Hook para criar tipo de relacionamento de pessoa
 */
export function useCreatePersonRelationshipType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PersonRelationshipTypeCreate) => personRelationshipTypeApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.relationshipTypes() });
    },
  });
}

/**
 * Hook para atualizar tipo de relacionamento de pessoa
 */
export function useUpdatePersonRelationshipType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PersonRelationshipTypeUpdate }) =>
      personRelationshipTypeApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.relationshipType(id) });
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.relationshipTypesList() });
    },
  });
}

/**
 * Hook para deletar tipo de relacionamento de pessoa
 */
export function useDeletePersonRelationshipType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => personRelationshipTypeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.relationshipTypes() });
    },
  });
}

// =============================================================================
// Family Relationship Type Hooks
// =============================================================================

/**
 * Hook para listar tipos de relacao familiar
 */
export function useFamilyRelationshipTypesList(params?: AccessListParams) {
  return useQuery({
    queryKey: accessTypeKeys.familyRelationshipTypesList(params),
    queryFn: () => familyRelationshipTypeApi.list(params || {}),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para obter um tipo de relacao familiar
 */
export function useFamilyRelationshipType(id: number) {
  return useQuery({
    queryKey: accessTypeKeys.familyRelationshipType(id),
    queryFn: () => familyRelationshipTypeApi.get(id),
    enabled: id > 0,
  });
}

/**
 * Hook para criar tipo de relacao familiar
 */
export function useCreateFamilyRelationshipType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FamilyRelationshipTypeCreate) => familyRelationshipTypeApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.familyRelationshipTypes() });
    },
  });
}

/**
 * Hook para atualizar tipo de relacao familiar
 */
export function useUpdateFamilyRelationshipType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: FamilyRelationshipTypeUpdate }) =>
      familyRelationshipTypeApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.familyRelationshipType(id) });
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.familyRelationshipTypesList() });
    },
  });
}

/**
 * Hook para deletar tipo de relacao familiar
 */
export function useDeleteFamilyRelationshipType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => familyRelationshipTypeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.familyRelationshipTypes() });
    },
  });
}

// =============================================================================
// Login Log Hooks
// =============================================================================

/**
 * Hook para listar logs de login
 */
export function useLoginLogsList(params?: LoginLogParams) {
  return useQuery({
    queryKey: accessTypeKeys.loginLogsList(params),
    queryFn: () => listLoginLogs(params || {}),
    staleTime: 1 * 60 * 1000, // 1 minute (logs change frequently)
  });
}

/**
 * Hook para dados agregados de login (gráficos)
 */
export function useLoginLogAggregation(params?: LoginLogParams) {
  return useQuery({
    queryKey: accessTypeKeys.loginLogAggregation(params),
    queryFn: () => getLoginLogAggregation(params || {}),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook para listar tipos de ações de login
 */
export function useLoginLogActions() {
  return useQuery({
    queryKey: accessTypeKeys.loginLogActions(),
    queryFn: listLoginLogActions,
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
  });
}

// =============================================================================
// Person Login Hooks (Gerenciamento de Logins)
// =============================================================================

/**
 * Hook para listar logins de pessoas
 */
export function usePersonLoginsList(params?: PersonLoginListParams) {
  return useQuery({
    queryKey: accessTypeKeys.personLoginsList(params),
    queryFn: () => personLoginApi.list(params || {}),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook para obter um login por ID
 */
export function usePersonLogin(id: number) {
  return useQuery({
    queryKey: accessTypeKeys.personLogin(id),
    queryFn: () => personLoginApi.get(id),
    enabled: id > 0,
  });
}

/**
 * Hook para criar login
 */
export function useCreatePersonLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PersonLoginCreate) => personLoginApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.personLogins() });
    },
  });
}

/**
 * Hook para atualizar login
 */
export function useUpdatePersonLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PersonLoginUpdate }) =>
      personLoginApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.personLogin(id) });
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.personLoginsList() });
    },
  });
}

/**
 * Hook para deletar login
 */
export function useDeletePersonLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => personLoginApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.personLogins() });
    },
  });
}

/**
 * Hook para reset de senha
 */
export function useResetPersonLoginPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newPassword, forceChange = true }: { id: number; newPassword: string; forceChange?: boolean }) =>
      personLoginApi.resetPassword(id, newPassword, forceChange),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.personLogin(id) });
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.personLoginsList() });
    },
  });
}

/**
 * Hook para alternar status do login
 */
export function useTogglePersonLoginStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => personLoginApi.toggleStatus(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.personLogin(id) });
      queryClient.invalidateQueries({ queryKey: accessTypeKeys.personLoginsList() });
    },
  });
}

/**
 * Hook para listar perfis disponíveis.
 *
 * Endpoint exige autenticação (current_user). Sem `enabled: !!user` o hook
 * dispara em qualquer mount — incluindo durante boot da app antes do JWT
 * estar pronto, gerando 401 no console.
 */
export function useAvailableProfiles() {
  const { user } = useAuth();
  return useQuery({
    queryKey: accessTypeKeys.availableProfiles(),
    queryFn: personLoginApi.listAvailableProfiles,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user,
  });
}

// =============================================================================
// Auth Assignment Hooks (Gerenciar Acessos)
// =============================================================================

import { authAssignmentApi } from '@/services/accessApi';
import type { AuthAssignmentListParams, AuthAssignmentCreate } from '@/types/access';

export const authAssignmentKeys = {
  all: ['authAssignments'] as const,
  list: (params?: AuthAssignmentListParams) => [...authAssignmentKeys.all, 'list', params] as const,
};

/**
 * Hook para listar atribuições de acesso
 */
export function useAuthAssignmentsList(params?: AuthAssignmentListParams) {
  return useQuery({
    queryKey: authAssignmentKeys.list(params),
    queryFn: () => authAssignmentApi.list(params || {}),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook para criar atribuição de acesso
 */
export function useCreateAuthAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AuthAssignmentCreate) => authAssignmentApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authAssignmentKeys.all });
    },
  });
}

/**
 * Hook para deletar atribuição de acesso
 */
export function useDeleteAuthAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemname, userid }: { itemname: string; userid: number }) =>
      authAssignmentApi.delete(itemname, userid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authAssignmentKeys.all });
    },
  });
}

// =============================================================================
// Export Types
// =============================================================================

export type {
  PersonImageType,
  PersonImageTypeCreate,
  PersonImageTypeUpdate,
  PersonRelationshipType,
  PersonRelationshipTypeCreate,
  PersonRelationshipTypeUpdate,
  FamilyRelationshipType,
  FamilyRelationshipTypeCreate,
  FamilyRelationshipTypeUpdate,
  AccessListParams,
  LoginLogEntry,
  LoginLogStats,
  LoginLogResponse,
  LoginLogParams,
  LoginLogAction,
  PersonLoginListParams,
  PersonLoginListItem,
  PersonLoginCreate,
  PersonLoginUpdate,
  PersonLoginResponse,
  AvailableProfile,
  // Auth Assignments
  AuthAssignmentItem,
  AuthAssignmentListParams,
  AuthAssignmentCreate,
} from '@/types/access';
